import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { categories, categoryMap } from "@/mocks/categories";
import { articles } from "@/mocks/articles";
import Breadcrumb from "@/components/feature/Breadcrumb";

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || "zuixin-baoliao");

  useEffect(() => {
    if (slug) {
      setActiveCategory(slug);
    }
  }, [slug]);

  const category = categoryMap[activeCategory];
  const filteredArticles = articles.filter((a) => a.category === activeCategory);

  const webpageSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: category?.name || "分类",
    description: category?.description || "",
    url: `https://51cgheibao.com/category/${activeCategory}`,
    inLanguage: "zh-CN",
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(webpageSchema)}</script>

      <main className="pt-20">
        <section className="py-8 md:py-12 px-4 md:px-8 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: "首页", href: "/" },
                { label: category?.name || "分类" },
              ]}
            />

            <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mt-6 mb-2 font-heading">
              {category?.name}
            </h1>
            <p className="text-sm text-foreground-600">
              {category?.description}
            </p>
          </div>
        </section>

        <section className="py-4 md:py-6 px-4 md:px-8 bg-background-50 border-b border-background-200 sticky top-16 z-30">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-2 overflow-x-auto pb-1">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  onClick={() => setActiveCategory(cat.slug)}
                  className={`flex items-center gap-1.5 px-4 py-2 text-sm rounded-full transition-all cursor-pointer whitespace-nowrap ${
                    activeCategory === cat.slug
                      ? "bg-primary-500 text-background-50 font-medium"
                      : "bg-background-100 text-foreground-600 hover:bg-background-200"
                  }`}
                >
                  <i className={`${cat.icon} text-sm`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 px-4 md:px-8 bg-background-50">
          <div className="max-w-7xl mx-auto">
            {filteredArticles.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.slug}`}
                    className="group bg-background-100 rounded-xl overflow-hidden hover:scale-[1.02] transition-all duration-300 cursor-pointer"
                  >
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={article.imageUrl}
                        alt={article.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                      {article.isHot && (
                        <div className="absolute top-3 left-3">
                          <span className="px-2.5 py-1 text-xs font-medium bg-primary-500 text-background-50 rounded-full">
                            热门
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="p-5">
                      <div className="flex items-center gap-3 text-xs text-foreground-500 mb-3 flex-wrap">
                        <span className="flex items-center gap-1">
                          <i className="ri-eye-line"></i>
                          {article.views.toLocaleString()}
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-time-line"></i>
                          {article.readTime}
                        </span>
                        <span>{article.publishedAt}</span>
                      </div>
                      <h2 className="text-base font-semibold text-foreground-950 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors font-heading leading-snug">
                        {article.title}
                      </h2>
                      <p className="text-sm text-foreground-600 line-clamp-2 leading-relaxed">
                        {article.excerpt}
                      </p>
                      <div className="flex flex-wrap gap-1.5 mt-4">
                        {article.tags.map((tag) => (
                          <span
                            key={tag}
                            className="px-2 py-0.5 text-xs bg-secondary-100 text-secondary-700 rounded-md"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                  <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">该分类下暂无文章</p>
              </div>
            )}
          </div>
        </section>
      </main>
    </>
  );
}